import type { ProcessedLayer } from '../types'

export interface StackupSide {
  svg: string
  width?: number | string
  height?: number | string
  units?: string
}

export interface StackupResult {
  top: StackupSide
  bottom: StackupSide
  layers: ProcessedLayer[]
}

export interface StackupLayerInput {
  filename: string
  gerber: string
  type?: string
  side?: string
}

export interface StackupOptions {
  color?: Record<string, string>
  useOutline: boolean
}

interface WorkerReply {
  id: number
  ok: boolean
  stackup?: StackupResult
  error?: string
}

interface Pending {
  resolve: (value: StackupResult) => void
  reject: (reason: Error) => void
  timer: number
}

let worker: Worker | null = null
let workerBroken = false
let nextId = 0
const pending = new Map<number, Pending>()

function rejectPending(reason: Error): void {
  for (const entry of pending.values()) {
    window.clearTimeout(entry.timer)
    entry.reject(reason)
  }
  pending.clear()
}

function ensureWorker(): Worker | null {
  if (workerBroken) return null
  if (worker) return worker
  try {
    const instance = new Worker(new URL('../workers/stackup.worker.ts', import.meta.url), {
      type: 'classic',
    })
    instance.onmessage = (event: MessageEvent<WorkerReply>): void => {
      const reply = event.data
      const entry = pending.get(reply.id)
      if (!entry) return
      pending.delete(reply.id)
      window.clearTimeout(entry.timer)
      if (reply.ok && reply.stackup) entry.resolve(reply.stackup)
      else entry.reject(new Error(reply.error ?? 'stackup worker failed'))
    }
    instance.onerror = (): void => {
      workerBroken = true
      rejectPending(new Error('stackup worker crashed'))
      instance.terminate()
      if (worker === instance) worker = null
    }
    worker = instance
  } catch {
    workerBroken = true
    worker = null
  }
  return worker
}

/**
 * Parse the Gerber stack in a worker. Resolves with cloned SVG strings and the
 * per-layer converter views the 3D build reuses; the raw converter streams do
 * not cross the worker boundary.
 */
export function renderStackupInWorker(
  layers: StackupLayerInput[],
  options: StackupOptions,
  timeoutMs = 120_000,
): Promise<StackupResult> {
  const instance = ensureWorker()
  if (!instance) return Promise.reject(new Error('stackup worker unavailable'))
  return new Promise<StackupResult>((resolve, reject) => {
    const id = ++nextId
    const timer = window.setTimeout(() => {
      pending.delete(id)
      reject(new Error('stackup worker timeout'))
    }, timeoutMs)
    pending.set(id, { resolve, reject, timer })
    try {
      instance.postMessage({ id, layers, options })
    } catch (error) {
      pending.delete(id)
      window.clearTimeout(timer)
      reject(error instanceof Error ? error : new Error('stackup worker post failed'))
    }
  })
}
